import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";
import {
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import { AttachFile, Colorize, DeleteOutline } from "@material-ui/icons";
import { QueueOptions } from "../QueueOptions";
import SchedulesForm from "../SchedulesForm";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    flex: 1,
    margin: theme.spacing(1),
    width: "calc(100% - 16px)",
    [theme.breakpoints.up("sm")]: {
      width: "auto",
    },
  },
  multilineField: {
    margin: theme.spacing(1),
    width: "calc(100% - 16px)",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    width: "calc(100% - 16px)",
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  dialogContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  actions: {
    flexDirection: "column",
    gap: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row-reverse",
    },
  },
  tabHint: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    fontStyle: "italic",
  },
}));

const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("queueModal.validation.tooShort"))
    .max(50, i18n.t("queueModal.validation.tooLong"))
    .required(i18n.t("queueModal.validation.required")),
  color: Yup.string()
    .min(3, i18n.t("queueModal.validation.tooShort"))
    .max(9, i18n.t("queueModal.validation.tooLong"))
    .required(i18n.t("queueModal.validation.required")),
  greetingMessage: Yup.string(),
});

const QueueModal = ({ open, onClose, queueId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    color: "",
    greetingMessage: "",
    outOfHoursMessage: "",
    orderQueue: "",
    integrationId: "",
    promptId: "",
  };

  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [queue, setQueue] = useState(initialState);
  const [tab, setTab] = useState(0);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const [schedules, setSchedules] = useState([
    { weekday: "Segunda-feira", weekdayEn: "monday",    startTime: "08:00", endTime: "18:00" },
    { weekday: "Terça-feira",   weekdayEn: "tuesday",   startTime: "08:00", endTime: "18:00" },
    { weekday: "Quarta-feira",  weekdayEn: "wednesday", startTime: "08:00", endTime: "18:00" },
    { weekday: "Quinta-feira",  weekdayEn: "thursday",  startTime: "08:00", endTime: "18:00" },
    { weekday: "Sexta-feira",   weekdayEn: "friday",    startTime: "08:00", endTime: "18:00" },
    { weekday: "Sábado",        weekdayEn: "saturday",  startTime: "08:00", endTime: "12:00" },
    { weekday: "Domingo",       weekdayEn: "sunday",    startTime: "00:00", endTime: "00:00" },
  ]);

  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [prompts, setPrompts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    api.get(`/settings`).then(({ data }) => {
      if (Array.isArray(data)) {
        const scheduleType = data.find((d) => d.key === "scheduleType");
        if (scheduleType) {
          setSchedulesEnabled(scheduleType.value === "queue");
        }
      }
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queueIntegration");
        setIntegrations(data.queueIntegrations);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!queueId) return;
      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setQueue((prevState) => ({ ...prevState, ...data }));
        setSelectedPrompt(data.promptId || null);
        setSchedules(data.schedules || schedules);
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setQueue(initialState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueId, open]);

  // Reset to first tab when modal opens
  useEffect(() => {
    if (open) setTab(0);
  }, [open]);

  const handleClose = () => {
    onClose();
    setQueue(initialState);
    setAttachment(null);
    if (attachmentFile.current) attachmentFile.current.value = null;
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      if (attachmentFile.current) attachmentFile.current.value = null;
    }

    if (queue.mediaPath) {
      await api.delete(`/queue/${queue.id}/media-upload`);
      setQueue((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("queueModal.toasts.deleted"));
    }
    setConfirmationOpen(false);
  };

  const handleSaveQueue = async (values) => {
    try {
      const payload = {
        ...values,
        schedules,
        promptId: selectedPrompt || null,
      };

      if (queueId) {
        await api.put(`/queue/${queueId}`, payload);
      } else {
        const { data } = await api.post("/queue", payload);
        queueId = data.id;
      }

      if (attachment && queueId) {
        const formData = new FormData();
        formData.append("file", attachment);
        await api.post(`/queue/${queueId}/media-upload`, formData);
      }

      toast.success("Departamento salvo com sucesso");
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveSchedules = (values) => {
    toast.info("Clique em salvar para registrar as alterações");
    setSchedules(values);
    setTab(0);
  };

  const handleChangePrompt = (e) => {
    setSelectedPrompt(e.target.value);
  };

  const isSchedulesTab = schedulesEnabled && tab === 3;

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("queueModal.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("queueModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <Dialog
        maxWidth="md"
        fullWidth
        open={open}
        onClose={handleClose}
        scroll="paper"
      >
        <DialogTitle>
          {queueId
            ? i18n.t("queueModal.title.edit")
            : i18n.t("queueModal.title.add")}
        </DialogTitle>

        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Geral" />
          <Tab label="Mensagens" />
          <Tab label="Bot / Automação" />
          {schedulesEnabled && <Tab label="Horários" />}
        </Tabs>

        {/* Formik wraps tabs 0, 1, 2 — stays mounted for all tabs to preserve values */}
        <Formik
          initialValues={queue}
          enableReinitialize
          validationSchema={QueueSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQueue(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              {!isSchedulesTab && (
                <DialogContent dividers className={classes.dialogContent}>

                  {/* ── Tab 0: Geral ── */}
                  {tab === 0 && (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          label={i18n.t("queueModal.form.name")}
                          autoFocus
                          name="name"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("queueModal.form.color")}
                          name="color"
                          id="color"
                          error={touched.color && Boolean(errors.color)}
                          helperText={touched.color && errors.color}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <div
                                  style={{ backgroundColor: values.color }}
                                  className={classes.colorAdorment}
                                />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => setColorPickerModalOpen(true)}
                              >
                                <Colorize />
                              </IconButton>
                            ),
                          }}
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("queueModal.form.orderQueue")}
                          name="orderQueue"
                          type="number"
                          inputProps={{ min: 0 }}
                          error={touched.orderQueue && Boolean(errors.orderQueue)}
                          helperText={touched.orderQueue && errors.orderQueue}
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  )}

                  {/* ── Tab 1: Mensagens ── */}
                  {tab === 1 && (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          label={i18n.t("queueModal.form.greetingMessage")}
                          name="greetingMessage"
                          multiline
                          rows={5}
                          fullWidth
                          error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                          helperText={touched.greetingMessage && errors.greetingMessage}
                          variant="outlined"
                        />
                      </Grid>

                      {schedulesEnabled && (
                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueModal.form.outOfHoursMessage")}
                            name="outOfHoursMessage"
                            multiline
                            rows={5}
                            fullWidth
                            error={touched.outOfHoursMessage && Boolean(errors.outOfHoursMessage)}
                            helperText={touched.outOfHoursMessage && errors.outOfHoursMessage}
                            variant="outlined"
                          />
                        </Grid>
                      )}

                      {(queue.mediaPath || attachment) && (
                        <Grid item xs={12}>
                          <Button startIcon={<AttachFile />} color="default">
                            {attachment ? attachment.name : queue.mediaName}
                          </Button>
                          <IconButton
                            onClick={() => setConfirmationOpen(true)}
                            color="secondary"
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  )}

                  {/* ── Tab 2: Bot / Automação ── */}
                  {tab === 2 && (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography className={classes.tabHint}>
                          Configure um fluxo de bot ou IA para atender automaticamente neste departamento.
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl
                          variant="outlined"
                          fullWidth
                          error={touched.integrationId && Boolean(errors.integrationId)}
                        >
                          <InputLabel>{i18n.t("queueModal.form.integrationId")}</InputLabel>
                          <Field
                            as={Select}
                            name="integrationId"
                            label={i18n.t("queueModal.form.integrationId")}
                            value={values.integrationId || ""}
                          >
                            <MenuItem value="">Nenhum</MenuItem>
                            {integrations.map((integration) => (
                              <MenuItem key={integration.id} value={integration.id}>
                                {integration.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                          <InputLabel>{i18n.t("whatsappModal.form.prompt")}</InputLabel>
                          <Select
                            name="promptId"
                            value={selectedPrompt || ""}
                            onChange={handleChangePrompt}
                            label={i18n.t("whatsappModal.form.prompt")}
                            MenuProps={{
                              anchorOrigin: { vertical: "bottom", horizontal: "left" },
                              transformOrigin: { vertical: "top", horizontal: "left" },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem value=""><em>Nenhum prompt</em></MenuItem>
                            {prompts.map((prompt) => (
                              <MenuItem key={prompt.id} value={prompt.id}>
                                {prompt.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <QueueOptions queueId={queueId} />
                      </Grid>
                    </Grid>
                  )}

                </DialogContent>
              )}

              {!isSchedulesTab && (
                <DialogActions className={classes.actions}>
                  {tab === 1 && !attachment && !queue.mediaPath && (
                    <Button
                      color="primary"
                      onClick={() => attachmentFile.current.click()}
                      disabled={isSubmitting}
                      variant="outlined"
                      fullWidth
                    >
                      {i18n.t("queueModal.buttons.attach")}
                    </Button>
                  )}
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting}
                    variant="outlined"
                    fullWidth
                  >
                    {i18n.t("queueModal.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    fullWidth
                    className={classes.btnWrapper}
                  >
                    {queueId
                      ? i18n.t("queueModal.buttons.okEdit")
                      : i18n.t("queueModal.buttons.okAdd")}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </DialogActions>
              )}

              <ColorPicker
                open={colorPickerModalOpen}
                handleClose={() => setColorPickerModalOpen(false)}
                onChange={(color) => {
                  setFieldValue("color", color);
                  setColorPickerModalOpen(false);
                }}
              />
            </Form>
          )}
        </Formik>

        {/* ── Tab 3: Horários ── (outside Formik to avoid nested forms) */}
        {isSchedulesTab && (
          <Paper style={{ padding: 20 }}>
            <SchedulesForm
              loading={false}
              onSubmit={handleSaveSchedules}
              initialValues={schedules}
              labelSaveButton="Atualizar"
            />
          </Paper>
        )}

        {/* Input oculto para anexos */}
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={handleAttachmentFile}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default QueueModal;
